/**
 * One broker's card on the Broker Accounts page (Broker Integration — Phase
 * 1). Always renders for all 3 supported brokers, whether or not the user
 * has ever connected that one — `account` is `undefined` for a broker with
 * no `broker_accounts` row yet, in which case the only available action is
 * "Connect".
 */
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import type { BrokerAccountRead, BrokerName } from "@/types";
import { API_HEALTH_COLOR, BROKER_META, CONNECTION_STATUS_META, formatDateTime } from "./colors";

interface BrokerAccountCardProps {
  broker: BrokerName;
  account?: BrokerAccountRead | null;
  /** Disables the "Set as Default" action while another card's default
   * request is in flight, so two default changes can never race. */
  defaultBusy: boolean;
  onConnect: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
  onViewDetails: () => void;
  onSetDefault: () => void;
}

export function BrokerAccountCard({
  broker,
  account,
  defaultBusy,
  onConnect,
  onReconnect,
  onDisconnect,
  onViewDetails,
  onSetDefault,
}: BrokerAccountCardProps) {
  const meta = BROKER_META[broker];
  const statusMeta = account ? CONNECTION_STATUS_META[account.connection_status] : CONNECTION_STATUS_META.DISCONNECTED;
  const isConnected = account?.is_connected ?? false;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
          <Avatar sx={{ bgcolor: meta.avatarColor, fontWeight: 700 }}>{meta.shortLabel}</Avatar>
          <Box flexGrow={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              {meta.displayName}
            </Typography>
            <Chip
              size="small"
              color={statusMeta.color}
              label={`${statusMeta.dot} ${statusMeta.label}`}
              sx={{ mt: 0.5 }}
            />
          </Box>
          {account && (
            <Tooltip title={isConnected ? (account.is_default ? "Default broker" : "Set as default broker") : "Connect this broker to set it as default"}>
              <span>
                <Button
                  size="small"
                  color="warning"
                  disabled={!isConnected || defaultBusy}
                  onClick={onSetDefault}
                  startIcon={account.is_default ? <StarIcon /> : <StarBorderIcon />}
                  sx={{ minWidth: 0 }}
                >
                  {account.is_default ? "Default" : ""}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>

        {account ? (
          <Stack spacing={0.75} sx={{ mb: 1 }}>
            <CardField label="Connected User ID" value={account.broker_user_id} />
            <CardField label="Client ID" value={account.client_id} />
            <CardField label="Account Name" value={account.account_name} />
            <CardField label="Account Type" value={account.account_type} />
            <CardField label="Last Login Time" value={formatDateTime(account.last_login)} />
            <CardField label="Session Expiry" value={formatDateTime(account.session_expiry)} />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                API Health
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color={API_HEALTH_COLOR[account.api_health]}
                icon={account.api_health === "HEALTHY" ? <CheckCircleIcon fontSize="small" /> : undefined}
                label={account.api_health}
              />
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Not connected yet. Connect your {meta.displayName} account to get TekInvest ready for future
            algo trading — no orders are placed in this phase.
          </Typography>
        )}
      </CardContent>

      <Box sx={{ p: 2, pt: 0 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {!account && (
            <Button variant="contained" size="small" onClick={onConnect}>
              Connect
            </Button>
          )}
          {account && (
            <Button variant="outlined" size="small" onClick={onReconnect}>
              Reconnect
            </Button>
          )}
          {account && isConnected && (
            <Button variant="outlined" size="small" color="error" onClick={onDisconnect}>
              Disconnect
            </Button>
          )}
          {account && (
            <Button variant="text" size="small" onClick={onViewDetails}>
              View Details
            </Button>
          )}
        </Stack>
      </Box>
    </Card>
  );
}

function CardField({ label, value }: { label: string; value?: string | null }) {
  return (
    <Box display="flex" justifyContent="space-between" gap={2}>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={600} sx={{ textAlign: "right", overflow: "hidden", textOverflow: "ellipsis" }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default BrokerAccountCard;
